/**
 * «Включите вход по лицу» — offered once, on the home screen.
 *
 * The iOS app asks right after the first sign-in, on a screen of its own. A modal here would be
 * worse: somebody who just signed in came to order fuel, and a dialog between them and that is a
 * toll. A card that can be dismissed says the same thing and costs nothing to ignore.
 *
 * Asked once per account per device. «Не сейчас» is remembered; signing in as somebody else
 * resets it, because for that person it has not been asked yet.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/auth';
import {
  dismissPasskeyOffer,
  enrollPasskey,
  isEnrolledHere,
  passkeyOfferDismissed,
  passkeysAvailable,
} from '@/entities/session/passkey';
import { Button } from '@/shared/ui/Button';
import { CloseIcon } from '@/shared/ui/icons';

export function PasskeyOffer() {
  const { profile } = useAuth();

  /** `null` until the device and the server have both answered — nothing is rendered before that. */
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Cheap local answers first: no round trip for a device that has already enrolled or has
    // already said no.
    if (isEnrolledHere() || passkeyOfferDismissed()) {
      setEligible(false);
      return;
    }
    let cancelled = false;
    void passkeysAvailable().then((ok) => {
      if (!cancelled) setEligible(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (eligible !== true || hidden || profile === null) return null;

  async function enable() {
    setBusy(true);
    setFailed(false);
    const ok = await enrollPasskey(profile?.email ?? '');
    setBusy(false);
    if (ok) setHidden(true);
    else setFailed(true);
  }

  function notNow() {
    dismissPasskeyOffer();
    setHidden(true);
  }

  return (
    <section className="flex flex-col gap-2.5 rounded-md bg-accent-soft px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <strong className="block text-[13px] font-bold leading-[1.3]">
            Включите вход по лицу
          </strong>
          <span className="block text-[13px] leading-[1.3] text-ink-soft">
            Быстрый вход без пароля. Ключ остаётся на этом устройстве — снимок лица мы не получаем.
          </span>
        </div>
        <button
          type="button"
          onClick={notNow}
          aria-label="Не сейчас"
          className="shrink-0 rounded-sm p-1 text-ink-soft"
        >
          <CloseIcon size={12} />
        </button>
      </div>

      {failed && (
        <span className="text-[12.5px] leading-[1.28] text-ink-soft">
          Не получилось — можно включить позже в профиле.
        </span>
      )}

      <div className="flex gap-2">
        <Button isLoading={busy} onClick={() => void enable()}>
          Включить
        </Button>
        <Button variant="ghost" disabled={busy} onClick={notNow}>
          Не сейчас
        </Button>
      </div>
    </section>
  );
}
